/**
 * CREATIVE DETONATOR - Shared Idea Card Renderer
 * Single source of truth for idea card HTML
 *
 * Used by:
 * - frontend/js/generator/phases/phase4-harvest.js (Generator Phase 4)
 * - frontend/features/dashboard/js/ideas.js (Dashboard Ideas page)
 *
 * This ensures both contexts render identical cards and can share
 * the same feature modules (art-director, image-generation, image-edit).
 *
 * Card Structure (all formats):
 * 1. Title
 * 2. THE IDEA (one-sentence concept)
 * 3. HEADLINE (with format-specific label: SUPER, CAPTION, CTA, etc.)
 * 4. SUBLINE (optional)
 * 5. VISUAL/EXECUTION (combined text blob with sectionLabel)
 * 6. WHY IT WORKS
 * 7. Lineage (SPARKED BY, THE TRUTH)
 */

// v68 - Import getIdeaVideoUrl for robust video persistence
// v73 - Dashboard cleanup: hide sections, move delete to header
// v74 - Redesigned delete icon to match outline style of other header buttons
// v75 - Added version history button to image/video sections
// v76 - Redesigned top section: FORMAT ICON | CATEGORY CODE | TITLE
//       Move GENERATE IMAGE into image section, add bulb icon to IDEA section
//       Hide #number in Dashboard context
// v77 - Migrated to centralized icons.js module for SVG icons
// v78 - Use data-mapper.js accessors for field normalization (Single Source of Truth)
import { getFormatIconPath } from '../core/utils.js';
import { Icons } from '../../../js/shared/icons.js';
import {
  getTriggerCode,
  getImageUrl,
  getVideoUrl,
  isSaved,
  getUpdatedAt,
  getCreatedAt
} from '../../../js/core/data-mapper.js';
import {
  renderHeadlineWithNav,
  renderCritiqueSection,
  renderRefinedBadge
} from '../features/art-director.js';
import { getIdeaImageUrl, supportsImageGeneration } from '../features/image-generation.js';
import { getIdeaVideoUrl } from '../features/video-generation.js';
import { renderHistoryButton } from '../features/version-history.js';
import { GeneratorState } from '../core/state.js';
// Centralized pricing - Single Source of Truth (see frontend/js/core/pricing.js)
import { IMAGE_COSTS } from '../../../js/core/pricing.js';

// Default image cost for "Generate Image" button (uses seedream as default)
const DEFAULT_IMAGE_COST = IMAGE_COSTS['seedream'];  // 5 credits

// ============================================================================
// CATEGORY COLORS
// ============================================================================

/**
 * Get category colors from window (set by main.js) or use fallback
 */
function getCategoryColors() {
  return window.CategoryColors || {
    'A': '#FF00FF', 'B': '#FF6600', 'C': '#00FFFF', 'D': '#00FF00',
    'E': '#9900FF', 'F': '#FFFF00', 'G': '#0066FF', 'H': '#FF0000',
    'I': '#FF6B9D', 'J': '#4ECDC4', 'K': '#9B59B6', 'L': '#E74C3C',
    'M': '#2ECC71', 'N': '#1ABC9C', 'O': '#F39C12'
  };
}

/**
 * Get appropriate text color (black or white) based on background color luminance
 * @param {string} hexColor - Hex color code
 * @returns {string} '#000000' for light backgrounds, '#FFFFFF' for dark
 */
function getCategoryTextColor(hexColor) {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/**
 * Get mini format icon path
 * @param {string} format - The format name
 * @returns {string} Path to mini icon (absolute from server root /frontend/)
 */
function getMiniFormatIcon(format) {
  const iconMap = {
    'print': '/assets/icons/format-icon-mini_0002_print-ooh.png',
    'social': '/assets/icons/format-icon-mini_0000_social-media.png',
    'video': '/assets/icons/format-icon-mini_0001_video.png',
    'digital': '/assets/icons/format-icon-mini_0003_digital.png',
    'campaign': '/assets/icons/format-icon-mini_0004_full-campaign.png',
    'experiential': '/assets/icons/format-icon-mini_0005_experimental.png'
  };
  return iconMap[format] || iconMap['print'];
}

// ============================================================================
// MAIN RENDER FUNCTION
// ============================================================================

/**
 * Render a complete idea card
 *
 * @param {Object} idea - The idea object (can be from Generator or DB)
 * @param {number} number - Display number (1-indexed)
 * @param {Object} options - Render options
 * @param {boolean} options.isDashboard - True if rendering in Dashboard context
 * @param {boolean} options.isSelected - True if card is visually selected
 * @param {boolean} options.isSaved - Override saved state
 * @param {string} options.categoryColor - Override category color
 * @returns {string} HTML string for the card
 */
export function renderIdeaCard(idea, number, options = {}) {
  const CategoryColors = getCategoryColors();

  // Normalize ID to string (DB IDs may be UUID, Generator IDs may be formatted)
  const id = String(idea.id);

  // Get trigger/category info
  const triggerCode = idea.trigger || idea.triggerCode || idea.trigger_code || 'A1';
  const categoryCode = triggerCode.charAt(0).toUpperCase();
  const categoryColor = options.categoryColor || CategoryColors[categoryCode] || '#888888';

  // Get format
  const format = (idea.format || 'print').toLowerCase();

  // Handle icon paths for different contexts
  // Dashboard is in /features/dashboard/pages/, Generator is in /pages/
  let formatIconPath = getFormatIconPath(format);
  if (options.isDashboard) {
    formatIconPath = '../' + formatIconPath;
  }

  // State flags - check both camelCase and snake_case variants
  const isSelected = options.isSelected || false;
  const isSaved = options.isSaved !== undefined ? options.isSaved : (idea.saved || idea.is_saved || false);
  const isRefined = idea.isRefined || idea.is_refined || false;

  // Content - normalize field names between DB and Generator
  const title = idea.title || idea.headline || 'Untitled';
  const visual = idea.visual || idea.text || '';
  const headline = idea.headline || '';
  const subline = idea.subline || '';
  const whyItWorks = idea.whyItWorks || idea.explanation || idea.why_it_works || idea.mechanism || '';
  const sectionLabel = idea.sectionLabel || 'VISUAL';
  const headlineLabel = idea.headlineLabel || 'HEADLINE';

  // Check for alternative headlines (from refinement)
  const hasAlternatives = idea.alternativeHeadlines && idea.alternativeHeadlines.length > 0;

  // Truncate title for header display (max 50 chars)
  const shortTitle = title.length > 50 ? title.substring(0, 47) + '...' : title;

  return `
    <div class="idea-card ${isSelected ? 'selected' : ''} ${isRefined ? 'refined' : ''}"
         data-id="${id}"
         data-format="${format}"
         data-number="#${number}"
         data-category="${categoryCode}"
         style="--cat-color: ${categoryColor}">

      <div class="idea-card-header">
        <div class="idea-card-topbar">
          <div class="idea-card-format">
            <img src="${getMiniFormatIcon(format)}" alt="" class="idea-card-format-mini-icon">
            <span class="idea-card-format-text">${format.toUpperCase()}</span>
          </div>
          <div class="idea-card-actions">
            <button class="btn-canvas-view" data-id="${id}" title="View Details">
              <img src="/assets/icons/idea-icon-mini.png" alt="" class="canvas-view-icon">
            </button>
            <button class="idea-copy-btn" data-id="${id}" title="Copy Prompt">
              ${Icons.copy()}
            </button>
            <button class="idea-save-btn ${isSaved ? 'saved' : ''}" data-id="${id}" title="${isSaved ? 'Unsave' : 'Save'}">
              <img src="/assets/icons/icon_0026_star-filled.png" class="pixel-icon star-black" alt="">
              <img src="/assets/icons/icon_0026_star-filled-white.png" class="pixel-icon star-white" alt="">
            </button>
            ${options.isDashboard ? `
            <button class="idea-delete-btn" data-id="${id}" title="Delete">
              ${Icons.delete()}
            </button>
            ` : ''}
          </div>
        </div>
        <div class="idea-card-titlebar">
          <span class="idea-card-category-badge" style="background: ${categoryColor}; color: ${getCategoryTextColor(categoryColor)}">${triggerCode}</span>
          <span class="idea-card-header-title" title="${title}">${shortTitle}</span>
          ${!options.isDashboard ? `<span class="idea-card-number">#${number}</span>` : ''}
          ${renderRefinedBadge(idea)}
        </div>
      </div>

      ${renderImageSection(id, format, options.isDashboard, idea)}

      <div class="idea-card-view-action">
        <button class="btn-view-idea-large" data-id="${id}">
          <img src="${options.isDashboard ? '/assets/icons/idea-icon-mini.png' : '/assets/icons/idea-icon-mini.png'}" alt="" class="view-idea-icon">
          <span>VIEW IDEA</span>
        </button>
      </div>

      <div class="idea-card-body">
        ${idea.idea ? `
          <div class="idea-card-section idea-card-idea-section">
            <div class="idea-card-idea-header">
              ${Icons.bulb({ size: 16, className: 'idea-bulb-icon' })}
              <span class="idea-card-idea-label">IDEA</span>
            </div>
            <p class="idea-card-idea">${idea.idea}</p>
          </div>
        ` : ''}

        ${headline && headlineLabel ? (hasAlternatives ? renderHeadlineWithNav(idea, id) : `
          <div class="idea-card-section idea-card-headline-section">
            <span class="idea-card-label">${headlineLabel}</span>
            <p class="idea-card-headline">"${headline}"</p>
          </div>
        `) : ''}

        ${subline ? `
          <div class="idea-card-section idea-card-subline-section">
            <span class="idea-card-label">SUBLINE</span>
            <p class="idea-card-subline">${subline}</p>
          </div>
        ` : ''}

        ${visual && !options.isDashboard ? `
          <div class="idea-card-section idea-card-visual-section">
            <span class="idea-card-label">${sectionLabel}</span>
            <p class="idea-card-visual">${visual}</p>
          </div>
        ` : ''}


        ${!options.isDashboard ? renderCritiqueSection(idea) : ''}

        ${(idea.sourceFragment || idea.sourceTruth) && !options.isDashboard ? `
          <div class="idea-card-lineage">
            ${idea.sourceFragment ? `
              <div class="idea-card-section idea-card-fragment-section">
                <span class="idea-card-label lineage-label">SPARKED BY</span>
                <p class="idea-card-fragment">"${idea.sourceFragment}"</p>
              </div>
            ` : ''}
            ${idea.sourceTruth ? `
              <div class="idea-card-section idea-card-truth-section">
                <span class="idea-card-label lineage-label">THE TRUTH</span>
                <p class="idea-card-truth">${idea.sourceTruth}</p>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
    </div>
  `;
}

// ============================================================================
// IMAGE SECTION
// ============================================================================

/**
 * Render the image/video section of a card
 * Priority: Video > Image > Placeholder
 *
 * If a video exists, render video player.
 * If only image exists, render image with actions.
 * If neither and format supports generation, show placeholder.
 *
 * For VIDEO format: Only show the section if there's actual media.
 * No placeholder for video cards - they use the GENERATE STORYBOARD button instead.
 *
 * @param {string} ideaId - The idea ID
 * @param {string} format - The idea format
 * @param {boolean} isDashboard - Whether in dashboard context
 * @param {Object} idea - The idea object (optional, for video_url access)
 * @returns {string} HTML string
 */
function renderImageSection(ideaId, format, isDashboard = false, idea = null) {
  // Get both URLs first
  const videoUrl = getIdeaVideoUrl(ideaId);
  const imageUrl = getIdeaImageUrl(ideaId);

  // Task 102: Determine which media to show based on latest generation timestamp
  // This ensures newly generated images display even when a video already exists
  const latestMedia = GeneratorState.latestMediaTimestamp?.[String(ideaId)];
  let showVideo = false;
  let showImage = false;

  if (latestMedia?.type === 'video' && videoUrl) {
    showVideo = true;
  } else if (latestMedia?.type === 'image' && imageUrl) {
    showImage = true;
  } else if (videoUrl) {
    // Fallback: show video if exists (legacy behavior for pre-existing data)
    showVideo = true;
  } else if (imageUrl) {
    showImage = true;
  }

  // Render video player if showing video
  if (showVideo) {
    return renderVideoPlayer(videoUrl, ideaId);
  }

  // If no image and format doesn't support generation, don't render section
  if (!imageUrl && !supportsImageGeneration(format)) {
    return '';
  }

  if (!imageUrl) {
    // VIDEO FORMAT: Don't show placeholder - just hide the section entirely
    // Video cards have their own storyboard generation workflow
    if (format === 'video') {
      return '';
    }

    // Only show placeholder for other formats that support image generation
    if (!supportsImageGeneration(format)) {
      return '';
    }
    // Show GENERATE IMAGE button in the image placeholder area
    // Cost badge uses centralized pricing
    return `
      <div class="idea-image-container no-image">
        <div class="image-placeholder-with-button">
          <button class="btn-generate-img btn-generate-img-placeholder" data-idea-id="${ideaId}">
            <span class="btn-gen-icon">
              ${Icons.generateImage({ size: 24 })}
            </span>
            <span class="btn-gen-text">GENERATE IMAGE (${DEFAULT_IMAGE_COST} C)</span>
          </button>
        </div>
      </div>
    `;
  }

  return `
    <div class="idea-image-container">
      <div class="generated-image-wrapper">
        <img src="${imageUrl}" alt="Generated image" class="generated-image" loading="lazy" />
        <div class="image-actions">
          ${renderHistoryButton(ideaId)}
          <button class="btn-img-action btn-regenerate-img" data-idea-id="${ideaId}" title="Generate Image">
            ${Icons.plus()}
          </button>
          <button class="btn-img-action btn-preview" data-url="${imageUrl}" title="View Full">
            ${Icons.fullscreen16()}
          </button>
          <button class="btn-img-action btn-download" data-url="${imageUrl}" title="Download">
            ${Icons.download()}
          </button>
          <button class="btn-img-action btn-edit" data-id="${ideaId}" title="Edit Image">
            ${Icons.editFilled()}
          </button>
          <button class="btn-img-action btn-make-video" data-id="${ideaId}" data-image-url="${imageUrl}" title="Make Video">
            ${Icons.makeVideo()}
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Render video player with controls
 * Shows video with preview, download, and regenerate buttons
 *
 * @param {string} videoUrl - The video URL
 * @param {string} ideaId - The idea ID (for regenerate button)
 * @returns {string} HTML string
 */
function renderVideoPlayer(videoUrl, ideaId) {
  // Get image URL for potential regeneration
  const imageUrl = getIdeaImageUrl(ideaId);

  return `
    <div class="idea-video-container">
      <div class="film-stripe film-stripe-top"></div>
      <div class="generated-video-wrapper">
        <video src="${videoUrl}"
               class="generated-video"
               controls
               preload="metadata"
               poster="">
          Your browser doesn't support video playback.
        </video>
        <div class="video-actions">
          ${renderHistoryButton(ideaId)}
          <button class="btn-img-action btn-video-preview" data-url="${videoUrl}" title="View Fullscreen">
            ${Icons.fullscreen16()}
          </button>
          <button class="btn-img-action btn-video-download" data-url="${videoUrl}" title="Download Video">
            ${Icons.download()}
          </button>
          ${imageUrl ? `
          <button class="btn-img-action btn-make-video" data-id="${ideaId}" data-image-url="${imageUrl}" title="Regenerate Video">
            ${Icons.makeVideo()}
          </button>
          ` : ''}
        </div>
      </div>
      <div class="film-stripe film-stripe-bottom"></div>
    </div>
  `;
}

// ============================================================================
// GENERATE BUTTON
// ============================================================================

/**
 * Render the generate/regenerate image button
 *
 * @param {string} ideaId - The idea ID
 * @param {string} format - The idea format
 * @returns {string} HTML string
 */
function renderGenerateButton(ideaId, format) {
  // Only for visual formats
  if (!supportsImageGeneration(format)) {
    return '';
  }

  const hasImage = !!getIdeaImageUrl(ideaId);
  // Cost badge uses centralized pricing
  const buttonText = hasImage ? `REGENERATE IMAGE (${DEFAULT_IMAGE_COST} C)` : `GENERATE IMAGE (${DEFAULT_IMAGE_COST} C)`;
  const buttonClass = hasImage ? 'btn-generate-img has-image' : 'btn-generate-img';

  return `
    <button class="${buttonClass}" data-idea-id="${ideaId}">
      <span class="btn-gen-icon">
        ${Icons.regenerate({ size: 16 })}
      </span>
      <span class="btn-gen-text">${buttonText}</span>
    </button>
  `;
}

// ============================================================================
// COMPACT RENDERERS (For dashboard lists)
// ============================================================================

/**
 * Normalize idea fields from various formats (DB, Generator, Dashboard)
 * Uses data-mapper.js accessors for consistent field resolution (Single Source of Truth)
 *
 * @param {Object} idea - Raw idea object
 * @returns {Object} Normalized idea object
 */
function normalizeIdeaFields(idea) {
  return {
    id: idea.id,
    triggerCode: getTriggerCode(idea),
    headline: idea.headline || idea.title || '',
    visual: idea.visual || idea.text || '',
    format: idea.format || 'print',
    imageUrl: getImageUrl(idea),
    videoUrl: getVideoUrl(idea),
    saved: isSaved(idea),
    updatedAt: getUpdatedAt(idea) || getCreatedAt(idea)
  };
}

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
function truncate(text, maxLength) {
  if (!text || text.length <= maxLength) return text || '';
  return text.substring(0, maxLength).trim() + '...';
}

/**
 * Get relative time string from date
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
function timeAgo(date) {
  if (!date) return '';
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
}

/**
 * Get category color from trigger code
 * @param {string} triggerCode - Trigger code (e.g., 'A1', 'B2')
 * @returns {string} Hex color
 */
function getCategoryColor(triggerCode) {
  const CategoryColors = getCategoryColors();
  const category = triggerCode?.charAt(0).toUpperCase() || 'A';
  return CategoryColors[category] || '#888888';
}

/**
 * Renders a compact idea row (for Dashboard Home recent ideas)
 *
 * @param {Object} idea - Idea object (normalized or raw)
 * @param {Object} options - Render options
 * @param {boolean} options.showStats - Show stats row (default: true)
 * @param {boolean} options.showActions - Show action buttons (default: true)
 * @returns {string} HTML string
 */
export function renderIdeaRow(idea, options = {}) {
  const {
    showStats = true,
    showActions = true
  } = options;

  const n = normalizeIdeaFields(idea);
  const categoryColor = getCategoryColor(n.triggerCode);

  // Build stats HTML
  let statsHTML = '';
  if (showStats) {
    const stats = [];
    if (n.saved) stats.push(`<span><img src="/assets/icons/icon_0026_star-filled.png" class="pixel-icon xs" alt=""> Saved</span>`);
    if (n.imageUrl) stats.push(`<span><img src="/assets/icons/icon_0046_picture-frame.png" class="pixel-icon xs" alt=""> Image</span>`);
    if (n.updatedAt) stats.push(`<span>${timeAgo(n.updatedAt)}</span>`);
    statsHTML = stats.length ? `<div class="idea-stats">${stats.join('')}</div>` : '';
  }

  return `
    <div class="idea-row" data-id="${n.id}">
      <div class="idea-thumb">
        ${n.imageUrl
          ? `<img src="${n.imageUrl}" alt="" style="width:100%;height:100%;object-fit:cover;">`
          : `<div style="width:100%;height:100%;background:linear-gradient(135deg,${categoryColor},#000);"></div>`
        }
      </div>
      <div class="idea-content">
        <div class="idea-meta">
          <span class="idea-trigger" style="background:${categoryColor};color:#000;">${n.triggerCode || '?'}</span>
          <span class="idea-brand">${n.format.toUpperCase()}</span>
        </div>
        <div class="idea-title">${n.headline || 'Untitled'}</div>
        <div class="idea-headline">${n.visual ? `"${truncate(n.visual, 80)}"` : ''}</div>
        ${statsHTML}
      </div>
      ${showActions ? `
        <div class="idea-actions">
          <button class="btn-icon-sm btn-view-idea" data-id="${n.id}" title="View">
            <img src="/assets/icons/icon_0007_eye.png" class="pixel-icon sm" alt="">
          </button>
        </div>
      ` : ''}
    </div>
  `;
}

/**
 * Renders a mini idea card (for session details, compact lists)
 *
 * @param {Object} idea - Idea object
 * @param {number} index - Index in list (for data-attribute)
 * @returns {string} HTML string
 */
export function renderMiniIdeaCard(idea, index = 0) {
  const n = normalizeIdeaFields(idea);
  const categoryColor = getCategoryColor(n.triggerCode);

  return `
    <div class="mini-idea-card ${n.saved ? 'saved' : ''}" data-idea-index="${index}" data-id="${n.id}">
      ${n.imageUrl ? `
        <div class="mini-idea-image">
          <img src="${n.imageUrl}" alt="">
        </div>
      ` : ''}
      <div class="mini-idea-content">
        <span class="mini-idea-trigger" style="background:${categoryColor};color:#000;">${n.triggerCode || '?'}</span>
        <p class="mini-idea-headline">${truncate(n.headline, 50)}</p>
      </div>
      ${n.saved ? '<img src="/assets/icons/icon_0026_star-filled.png" class="pixel-icon xs saved-badge" alt="">' : ''}
    </div>
  `;
}

// ============================================================================
// EXPORTS
// ============================================================================

// Export for use by phase4-harvest.js and dashboard/ideas.js
export { getCategoryColors, getCategoryColor, normalizeIdeaFields, truncate, timeAgo };

// Also expose to window for use by non-module scripts (dashboard home, sessions)
if (typeof window !== 'undefined') {
  window.IdeaCardRenderer = {
    renderIdeaCard,
    renderIdeaRow,
    renderMiniIdeaCard,
    getCategoryColors,
    getCategoryColor,
    normalizeIdeaFields,
    truncate,
    timeAgo
  };
}
